import axios from "axios";

const TOKEN_KEY = "garment_token";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://uniformapi.solapurmall.com/api",
  // 60s — quotation/SO saves can legitimately take 15-30s when building
  // rate snapshots and creating runtime products.  The old 15s default
  // was aborting valid requests mid-flight.
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

// ─── Request: inject Bearer token + start loading ──────────────────────────
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // A background poll (the support-ticket badge in the top bar) passes
    // { silent: true } so it does not flash the progress bar every minute.
    if (!config.silent) window.__garmentLoading?.start();
    return config;
  },
  (error) => {
    window.__garmentLoading?.done();
    return Promise.reject(error);
  }
);

// ─── Response: handle 401 globally + stop loading ─────────────────────────
API.interceptors.response.use(
  (response) => {
    if (!response.config?.silent) window.__garmentLoading?.done();
    return response;
  },
  (error) => {
    if (!error.config?.silent) window.__garmentLoading?.done();
    // A 401 normally means the stored token has expired, and we redirect to
    // the login page.  But when the 401 comes FROM the login attempt itself
    // (wrong password), we must NOT redirect — let the Login form catch the
    // error and show a proper "Invalid username or password" message.
    const url = error.config?.url || "";
    const isLoginRequest = /\/auth\/login\b/.test(url);
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("garment_session");
      window.location.href = "/";
    }
    // 403 = signed in but not allowed. The user's permissions may have just
    // changed, so let AuthContext re-read them (it throttles this).
    if (error.response?.status === 403 && !isLoginRequest) {
      window.dispatchEvent(new CustomEvent("permission-denied"));
    }
    // 402 = subscription expired/locked. Don't log the user out — just tell the
    // app to swap in the paywall (handled in AuthContext via this event).
    if (error.response?.status === 402) {
      window.dispatchEvent(new CustomEvent("subscription-locked"));
    }
    return Promise.reject(error);
  }
);

export default API;